import * as React from 'react'
import {Lang} from 'yougile-lang'
import {DataObjectId, DeadlineStickerValue} from 'yougile-model'
import {Sync} from '../../client/sync'
import {UserSettings} from '../../client/user-settings'
import {stickerCtl} from '../../extensions/sticker/sticker'
import {UserAvatar} from '../user-avatar'
import {UserName} from '../user-name'
import {TaskItem} from './task-item'

export type SortKey = 'manual' | 'creation' | 'deadline' | 'author' | 'assigned'

interface GroupedTasks {
  id: string
  title: React.ReactNode
  items: TaskItem[]
}

interface SortType {
  key: SortKey
  getName: () => string
  getComparator?: (page: string) => (a: TaskItem, b: TaskItem) => number
  getGrouping?: (page: string, allTasks: TaskItem[]) => GroupedTasks[]
}

export type TaskPage =
  | 'my'
  | 'by-me'
  | 'private'
  | 'by-people'
  | 'starred'
  | 'expired'
  | 'not-assigned'

export const getDefaultSortFromPage = (page: TaskPage): SortKey => {
  if (page === 'by-people' || page === 'by-me') {
    return 'assigned'
  } else if (page === 'expired') {
    return 'deadline'
  } else if (page === 'my' || page === 'private' || page === 'starred') {
    return 'manual'
  } else {
    return 'creation'
  }
}

const manualSort: SortType = {
  key: 'manual',
  getName: () => Lang.msg.taskPage.sortManual,
  getComparator: page => {
    const orderChange = UserSettings.getOrderChange(page + '-tasks')
    const order = UserSettings.getOrder(page + '-tasks')
    const map: Map<DataObjectId, number> = new Map()
    order.forEach((id, ix) => map.set(id, ix))
    const compare = (a: TaskItem, b: TaskItem) => {
      return (
        +b.starred - +a.starred ||
        (map.get(a.id) ?? (a.timestamp > orderChange ? -1 : Infinity)) -
          (map.get(b.id) ?? (b.timestamp > orderChange ? -1 : Infinity)) ||
        b.timestamp - a.timestamp
      )
    }
    return compare
  }
}

const creationSort: SortType = {
  key: 'creation',
  getName: () => Lang.msg.taskPage.sortCreateDate,
  getComparator: () => (a, b) => b.timestamp - a.timestamp
}

const deadlineSort: SortType = {
  key: 'deadline',
  getName: () => Lang.msg.taskPage.sortDeadline,
  getComparator: () => {
    const box = Sync.getCurrentSandbox()
    const deadlineSticker = stickerCtl.Deadline.getSticker(box)
    return (a, b) => {
      const da = a.stickers[deadlineSticker.id] as DeadlineStickerValue
      const db = b.stickers[deadlineSticker.id] as DeadlineStickerValue
      const dla = da && da.current ? da.current.deadline || 0 : 0
      const dlb = db && db.current ? db.current.deadline || 0 : 0
      if (dla - dlb === 0) {
        return b.timestamp - a.timestamp
      } else if (dla === 0) {
        return 1
      } else if (dlb === 0) {
        return -1
      } else {
        return dla - dlb
      }
    }
  }
}

const authorSort: SortType = {
  key: 'author',
  getName: () => Lang.msg.taskPage.sortAuthor,
  getGrouping: (_page, allTasks) => {
    const box = Sync.getCurrentSandbox()
    const users = box.getAllUsers(false)
    allTasks.sort((a, b) => b.timestamp - a.timestamp)
    const res = users
      .map(u => {
        const items = allTasks.filter(t => t.by === u.id)
        return {items, user: u}
      })
      .filter(({items}) => items.length > 0)
      .map(({items, user}): GroupedTasks => {
        const id = user.id
        return {
          id,
          title: (
            <>
              <UserAvatar userId={id} size={18} /> <UserName userId={id} />
            </>
          ),
          items
        }
      })
    const noUser = allTasks.filter(t => !t.by)
    if (noUser.length > 0) {
      res.push({
        id: 'not-set',
        title: Lang.msg.taskPage.userNotSet,
        items: noUser
      })
    }
    return res
  }
}

const assignedSort: SortType = {
  key: 'assigned',
  getName: () => Lang.msg.taskPage.sortAssigned,
  getGrouping: (_page, allTasks) => {
    const box = Sync.getCurrentSandbox()
    const users = box.getAllUsers(false)
    allTasks.sort((a, b) => b.timestamp - a.timestamp)
    const res = users
      .map(u => {
        const items = allTasks.filter(t => t.assigned.includes(u.id))
        return {items, user: u}
      })
      .filter(({items}) => items.length > 0)
      .map(({items, user}): GroupedTasks => {
        const id = user.id
        return {
          id,
          title: (
            <>
              <UserAvatar userId={id} size={18} /> <UserName userId={id} />
            </>
          ),
          items
        }
      })
    const noUser = allTasks.filter(t => t.assigned.length === 0)
    if (noUser.length > 0) {
      res.unshift({
        id: 'not-set',
        title: Lang.msg.taskPage.userNotSet,
        items: noUser
      })
    }
    return res
  }
}

export const sortTypes: Map<SortKey, SortType> = new Map()
;[manualSort, creationSort, deadlineSort, authorSort, assignedSort].forEach(s =>
  sortTypes.set(s.key, s)
)
